import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
let authToken = null;

export const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const setAuthToken = (token) => {
  authToken = token || null;
};

api.interceptors.request.use((config) => {
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  return config;
});

const apiError = (error, fallback) => {
  const message = error.response?.data?.error || error.message || fallback;
  return new Error(message);
};

export const authenticateUser = async (username, password) => {
  try {
    const { data } = await api.post('/signin', { username, password });
    setAuthToken(data.token);
    return {
      token: data.token,
      user_name: data.user.username,
      table_name: data.user.tenantSlug,
      tenant_name: data.user.tenantName,
      settings: data.user.settings || {},
    };
  } catch (error) {
    throw apiError(error, 'Authentication failed');
  }
};

export const fetchTableMetadata = async () => {
  try {
    const { data } = await api.get('/product-metadata');
    if (!data?.columns) throw new Error('Invalid metadata structure received from server');
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to fetch product metadata');
  }
};

export const fetchProducts = async () => {
  try {
    const { data } = await api.get('/products');
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to fetch products');
  }
};

export const createRecord = async (_tableName, fields, codeNumber) => {
  try {
    const { data } = await api.post('/products', { fields, codeNumber });
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to create product');
  }
};

export const fetchNextProductCode = async () => {
  try {
    const { data } = await api.get('/products/next-code');
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to load the next product code');
  }
};

export const updateRecord = async (_tableName, id, fields, ignoredFields = []) => {
  try {
    const processedFields = Object.fromEntries(
      Object.entries(fields).filter(([key]) => !ignoredFields.includes(key))
    );
    const { data } = await api.put(`/products/${id}`, { fields: processedFields });
    if (!data || typeof data !== 'object') throw new Error('Invalid response from server');
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to update product');
  }
};

export const deactivateRecord = async (_tableName, id) => {
  try {
    const { data } = await api.delete(`/products/${id}`);
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to deactivate product');
  }
};

export const changeProductCode = async (id, newCode) => {
  try {
    const { data } = await api.post(`/products/${id}/change-code`, { newCode });
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to change product code');
  }
};

export const validateBulkCodeChanges = async (rows) => {
  try {
    const { data } = await api.post('/products/bulk-code-changes/validate', { rows });
    return data.rows;
  } catch (error) {
    throw apiError(error, 'Failed to validate code changes');
  }
};

export const applyBulkCodeChanges = async (rows, sourceFilename) => {
  try {
    const { data } = await api.post('/products/bulk-code-changes/apply', { rows, sourceFilename });
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to process code changes');
  }
};

export const fetchBulkCodeChangeRuns = async () => {
  try {
    const { data } = await api.get('/products/bulk-code-changes/runs');
    return data.runs;
  } catch (error) {
    throw apiError(error, 'Failed to load import history');
  }
};

export const fetchBulkCodeChangeRun = async (runId) => {
  try {
    const { data } = await api.get(`/products/bulk-code-changes/runs/${runId}`);
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to load import details');
  }
};

export const undoBulkCodeChangeRun = async (runId) => {
  try {
    const { data } = await api.post(`/products/bulk-code-changes/runs/${runId}/undo`);
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to undo import');
  }
};

export const undoBulkCodeChangeEntry = async (runId, entryId) => {
  try {
    const { data } = await api.post(`/products/bulk-code-changes/runs/${runId}/entries/${entryId}/undo`);
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to undo row');
  }
};

export const forceSync = async () => {
  try {
    const { data } = await api.post('/products/force-sync');
    return data;
  } catch (error) {
    throw apiError(error, 'Failed to queue products for sync');
  }
};
